import { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { httpClient } from 'common/services';
import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { sipSettings } from 'features/sip/config';
import { setUserEmail } from 'features/user/store';
import { selectIsInCallerSession } from 'features/caller-session/store';
import { shouldSuppressOidcErrorNotification } from 'features/auth/helpers';
import {
  clearCognitoForcedLogoutRequest,
  tokenNotSetOnHeaders,
  tokenSetOnHeaders,
} from 'features/app/store/app/appSlice';
import { selectIsCognitoForcedLogoutRequested } from 'features/app/store/app/selectors';
import { resetMultiMode } from 'features/multi-mode/store';
import { ModeStorageService } from 'features/multi-mode/services';
import { showNotification } from 'features/notification/store';
import { PageRoute } from 'features/router/routes';

export const useAuthorization = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isInCallerSession = useAppSelector(selectIsInCallerSession);
  const isCognitoForcedLogoutRequested = useAppSelector(
    selectIsCognitoForcedLogoutRequested
  );
  const hasHandledCurrentErrorRef = useRef(false);
  const hasHandledUnauthorizedRequestRef = useRef(false);
  const isForcingCognitoLogoutRef = useRef(false);
  const {
    events,
    user,
    error,
    isLoading: isAuthLoading,
    isAuthenticated,
    removeUser,
    settings,
    signinRedirect,
    signoutRedirect,
  } = useAuth();
  const isTeamingLogoutNoticeRoute =
    location.pathname === PageRoute.VRS_TEAMING_LOGOUT_NOTICE;

  const setJwtToken = (token: string) => {
    sipSettings.update({ authorization_jwt: `Bearer ${token}` });
    httpClient.setHeader('Authorization', token);
    dispatch(tokenSetOnHeaders());
  };

  useEffect(() => {
    httpClient.setUnauthorizedRequestHandler(() => {
      if (
        hasHandledUnauthorizedRequestRef.current ||
        isInCallerSession ||
        isCognitoForcedLogoutRequested ||
        isTeamingLogoutNoticeRoute
      ) {
        return;
      }

      hasHandledUnauthorizedRequestRef.current = true;

      dispatch(tokenNotSetOnHeaders());
      dispatch(
        showNotification({
          severity: 'info',
          title: t('sessionExpired'),
        })
      );
      dispatch(resetMultiMode());
      ModeStorageService.clear();
      navigate(PageRoute.MAIN);
    });

    return () => {
      httpClient.setUnauthorizedRequestHandler(null);
    };
  }, [
    dispatch,
    isCognitoForcedLogoutRequested,
    isInCallerSession,
    isTeamingLogoutNoticeRoute,
    navigate,
    t,
  ]);

  useEffect(() => {
    return events.addSilentRenewError(() => {
      if (
        !isInCallerSession &&
        !isCognitoForcedLogoutRequested &&
        !isTeamingLogoutNoticeRoute
      ) {
        signinRedirect();
      }
      dispatch(tokenNotSetOnHeaders());
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isInCallerSession,
    isCognitoForcedLogoutRequested,
    isTeamingLogoutNoticeRoute,
  ]);

  useEffect(() => {
    if (isForcingCognitoLogoutRef.current || !isCognitoForcedLogoutRequested) {
      return;
    }

    const forceCognitoLogout = async () => {
      isForcingCognitoLogoutRef.current = true;
      dispatch(tokenNotSetOnHeaders());

      try {
        const redirectUri = settings.redirect_uri;
        const clientId = settings.client_id;
        const idTokenHint = user?.id_token;

        await removeUser();
        await signoutRedirect({
          id_token_hint: idTokenHint,
          post_logout_redirect_uri: redirectUri,
          extraQueryParams: {
            client_id: clientId,
            redirect_uri: redirectUri,
            logout_uri: redirectUri,
            response_type: settings.response_type ?? 'code',
            scope: settings.scope ?? 'openid profile email',
          },
        });
      } catch {
        dispatch(clearCognitoForcedLogoutRequest());
        isForcingCognitoLogoutRef.current = false;
        await signinRedirect({ prompt: 'login', max_age: 0 });
      }
    };

    void forceCognitoLogout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCognitoForcedLogoutRequested]);

  useEffect(() => {
    if (
      !isAuthLoading &&
      !isAuthenticated &&
      !isInCallerSession &&
      !isCognitoForcedLogoutRequested &&
      !isTeamingLogoutNoticeRoute &&
      !error // Don't auto-redirect if there's an authentication error
    ) {
      signinRedirect();
      dispatch(tokenNotSetOnHeaders());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    isAuthLoading,
    isInCallerSession,
    isCognitoForcedLogoutRequested,
    isTeamingLogoutNoticeRoute,
    error,
  ]);

  // Handle authentication errors by redirecting to main page (VRS/VRI selection)
  useEffect(() => {
    if (
      error &&
      !isInCallerSession &&
      !isAuthenticated &&
      !isCognitoForcedLogoutRequested &&
      !isTeamingLogoutNoticeRoute &&
      !hasHandledCurrentErrorRef.current
    ) {
      // Set the flag immediately before starting side effects to prevent race condition
      hasHandledCurrentErrorRef.current = true;

      // Show error notification to user
      if (!shouldSuppressOidcErrorNotification(error)) {
        dispatch(
          showNotification({
            severity: 'error',
            title: t('auth.signInAgain'),
          })
        );
      }
      // Clear the selected mode so user sees mode selection instead of auth again
      dispatch(resetMultiMode());
      // Also clear mode from localStorage to prevent auto-selection
      ModeStorageService.clear();
      // Navigate to main page
      navigate(PageRoute.MAIN);
    }
  }, [
    dispatch,
    error,
    isAuthenticated,
    isInCallerSession,
    isTeamingLogoutNoticeRoute,
    navigate,
    isCognitoForcedLogoutRequested,
    t,
  ]);

  // Reset the error handling flag when error is cleared or user becomes authenticated
  useEffect(() => {
    if (!error || isAuthenticated) {
      hasHandledCurrentErrorRef.current = false;
    }
  }, [error, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    hasHandledUnauthorizedRequestRef.current = false;
    httpClient.resetUnauthorizedRequestState();
  }, [isAuthenticated]);

  useEffect(() => {
    const token = user?.id_token;
    if (!token) {
      return;
    }

    setJwtToken(token);
    dispatch(setUserEmail(user.profile.email!));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return user?.id_token ?? null;
};
